import { useCallback, useEffect, useRef, useState } from "react";

export type DashboardPresenceSession = {
  client_id: string;
  user_id: number;
  login: string;
  email: string | null;
  topic: string;
  path: string;
  os: string;
  browser: string;
  ip: string;
  connected_at_ms: number;
  last_seen_ms: number;
  age_sec: number;
};

export type DashboardPresenceSummary = {
  tab_count: number;
  user_count: number;
  users: { login: string; tabs: number; topics: string[] }[];
};

export type UiLoadAdminState = {
  paused: boolean;
  message: string;
  since_ms: number | null;
  by_login: string | null;
};

type PresenceState = {
  sessions: DashboardPresenceSession[];
  summary: DashboardPresenceSummary | null;
  uiLoad: UiLoadAdminState | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastUpdated: number | null;
  savingUiLoad: boolean;
};

export function useAdminDashboardPresence(enabled: boolean, reloadKey?: string) {
  const [state, setState] = useState<PresenceState>({
    sessions: [],
    summary: null,
    uiLoad: null,
    loading: false,
    refreshing: false,
    error: null,
    lastUpdated: null,
    savingUiLoad: false,
  });
  const requestIdRef = useRef(0);

  const load = useCallback(async (opts?: { bustCache?: boolean }) => {
    const requestId = ++requestIdRef.current;
    setState((s) => ({
      ...s,
      loading: s.sessions.length === 0,
      refreshing: s.sessions.length > 0,
      error: null,
    }));

    const url = opts?.bustCache
      ? `/api/admin/dashboard-presence?_=${Date.now()}`
      : "/api/admin/dashboard-presence";

    try {
      const res = await fetch(url, { credentials: "same-origin", cache: "no-store" });
      if (requestId !== requestIdRef.current) return;

      if (res.status === 401 || res.status === 403) {
        setState({
          sessions: [],
          summary: null,
          uiLoad: null,
          loading: false,
          refreshing: false,
          error: "Not authorized",
          lastUpdated: null,
          savingUiLoad: false,
        });
        return;
      }
      if (!res.ok) {
        setState((s) => ({
          ...s,
          loading: false,
          refreshing: false,
          error: "Failed to load dashboard presence",
        }));
        return;
      }

      const data = await res.json();
      if (requestId !== requestIdRef.current) return;

      setState((s) => ({
        ...s,
        sessions: Array.isArray(data.sessions) ? data.sessions : [],
        summary: data.summary ?? null,
        uiLoad: data.ui_load ?? null,
        loading: false,
        refreshing: false,
        error: null,
        lastUpdated: Date.now(),
      }));
    } catch {
      if (requestId !== requestIdRef.current) return;
      setState((s) => ({
        ...s,
        loading: false,
        refreshing: false,
        error: "Failed to load dashboard presence",
      }));
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current += 1;
      setState({
        sessions: [],
        summary: null,
        uiLoad: null,
        loading: false,
        refreshing: false,
        error: null,
        lastUpdated: null,
        savingUiLoad: false,
      });
      return;
    }
    void load({ bustCache: true });
    const id = window.setInterval(() => void load({ bustCache: true }), 15_000);
    return () => window.clearInterval(id);
  }, [enabled, reloadKey, load]);

  const setUiPaused = useCallback(
    async (paused: boolean, message?: string) => {
      setState((s) => ({ ...s, savingUiLoad: true, error: null }));
      try {
        const res = await fetch("/api/admin/ui-load", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ paused, message }),
        });
        if (!res.ok) {
          setState((s) => ({
            ...s,
            savingUiLoad: false,
            error: "Failed to update UI pause state",
          }));
          return;
        }
        const data = (await res.json()) as UiLoadAdminState;
        setState((s) => ({
          ...s,
          uiLoad: data,
          savingUiLoad: false,
        }));
        await load({ bustCache: true });
      } catch {
        setState((s) => ({
          ...s,
          savingUiLoad: false,
          error: "Failed to update UI pause state",
        }));
      }
    },
    [load],
  );

  const reload = useCallback(() => load({ bustCache: true }), [load]);

  return { ...state, reload, setUiPaused };
}
